// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Agents browser, the contract (docs/konvo-agents/DESIGN.md, "Browser"; ClickUp
// 869fhhhzj). A page opens in a real browser on the viewer's device (the Konvo desktop app for now)
// while its traffic goes through Konvo and the machine's bridge and out from that machine, so
// localhost dev servers, staging and internal sites work as they do there. HTTPS stays end to end
// (CONNECT); Konvo only passes bytes on and keeps a "who opened what" list (no content) for the
// machine's owner. The owner picks what the browser may reach in Settings → Connections → Agents;
// Konvo and the bridge both hold to it. For now only the owner browses through their machines.

import {z} from 'zod';

/** What the browser may reach through a machine: the machine itself, its networks too, or anything. */
export const AGENT_BROWSER_LIMITS = ['machine', 'lan', 'all'] as const;
export type AgentBrowserLimit = (typeof AGENT_BROWSER_LIMITS)[number];
export const AgentBrowserLimitSchema = z.enum(AGENT_BROWSER_LIMITS);
/** A machine Konvo first sees opens only its own pages. */
export const DEFAULT_BROWSER_LIMIT: AgentBrowserLimit = 'machine';

/** The machine owner's own cap (`konvo-agents config`): a limit, or off. */
export type AgentBrowserCap = AgentBrowserLimit | 'off';
const RANK: Record<AgentBrowserCap, number> = {off: 0, machine: 1, lan: 2, all: 3};
/** The narrower of Konvo's setting and the machine's cap (no cap: Konvo's). */
export function narrowerBrowserLimit(limit: AgentBrowserLimit, cap: AgentBrowserCap | undefined): AgentBrowserCap {
	return cap !== undefined && RANK[cap] < RANK[limit] ? cap : limit;
}

/** What kind of address a page went to (konvo_agents/src/tunnel/reach.ts). */
export const AgentBrowserReachSchema = z.enum(['loopback', 'lan', 'public']);
export type AgentBrowserReach = z.infer<typeof AgentBrowserReachSchema>;

/**
 * POST /konvo/agents/machines/:id/browser/session -> where and how the desktop app connects its
 * tunnel. `token` is good once, for a minute; `path` is under the API (…/api/v1 + path, as wss).
 */
export const AgentBrowserSessionSchema = z.object({
	session: z.string().max(64),
	token: z.string().max(200),
	path: z.string().max(200),
	limit: AgentBrowserLimitSchema,
	expiresAt: z.string(),
});
export type AgentBrowserSession = z.infer<typeof AgentBrowserSessionSchema>;

/** A dev server running on the machine (T3 Code's own scan where it has one, else a quick look at common ports). */
export const AgentDevServerSchema = z.object({
	host: z.string().max(253),
	port: z.number().int().min(1).max(65535),
	url: z.string().max(300),
	/** The program listening, when known ("node", "vite"). */
	process: z.string().max(100).optional(),
	/** The page's title, when it answered with one. */
	title: z.string().max(200).optional(),
	/** The thread whose terminal started it, when the agent tool knows. */
	threadId: z.string().max(200).optional(),
	/** That thread's terminal it runs in (konvo-agents 0.58.0+): Stop sends Ctrl+C there. */
	terminalId: z.string().max(200).optional(),
});
export type AgentDevServer = z.infer<typeof AgentDevServerSchema>;

/** devServers op data, and GET /konvo/agents/machines/:id/browser/servers?threadId=. */
export const AgentDevServersSchema = z.object({
	servers: z.array(AgentDevServerSchema).max(50),
	/** 't3' when the agent tool's own scan found them; 'probe' when the bridge looked at common ports. */
	source: z.enum(['t3', 'probe']),
	scannedAt: z.string().max(40),
});
export type AgentDevServers = z.infer<typeof AgentDevServersSchema>;

/** The bridge's command args: dial the relay for this session, within this limit. */
export const AgentBrowserTunnelArgsSchema = z.object({
	session: z.string().min(1).max(64),
	token: z.string().min(1).max(200),
	limit: AgentBrowserLimitSchema,
});
export const AgentBrowserTunnelDataSchema = z.object({connected: z.literal(true)});

export const AgentDevServersArgsSchema = z.object({threadId: z.string().max(200).optional()});

/**
 * One line of the owner's "who opened what" list: a host:port someone browsed through the machine,
 * per browser session, with what moved. Konvo keeps 30 days and no content (no paths, no pages).
 */
export const AgentBrowserLogEntrySchema = z.object({
	at: z.string(),
	lastAt: z.string(),
	userId: z.string(),
	name: z.string().max(100),
	host: z.string().max(253),
	port: z.number().int(),
	/** Connections opened, and refused (by the limit or because nothing answered). */
	opened: z.number().int().nonnegative(),
	refused: z.number().int().nonnegative(),
	/** Why the last refused one was refused (BLOCKED, NOTHING_RUNNING, …). */
	refusal: z.string().max(40).optional(),
	bytesUp: z.number().int().nonnegative(),
	bytesDown: z.number().int().nonnegative(),
});
export type AgentBrowserLogEntry = z.infer<typeof AgentBrowserLogEntrySchema>;

/** GET /konvo/agents/machines/:id/browser/log -> the last 30 days, newest first. */
export const AgentBrowserLogSchema = z.object({entries: z.array(AgentBrowserLogEntrySchema).max(500)});
export type AgentBrowserLog = z.infer<typeof AgentBrowserLogSchema>;

/** How long the list keeps an entry. */
export const AGENT_BROWSER_LOG_DAYS = 30;
